import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonPrimary } from '../../../components/buttons/button-primary.component';
import { FilterPaneHeaderComponent } from '../../../components/filters/components/filter-pane-header/filter-pane-header.component';
import {
  hasFilterSelectedValue,
  useAppSingleFilterSelectedLabel,
} from '../../../components/filters/hooks/common.hook';
import { FILTER_NAMES } from '../../../components/filters/hooks/filter-names.constants';
import { useFilterResetCallback } from '../../../components/filters/hooks/use-filter-reset.hook';
import { FilterSelectedValue } from '../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useAppFilterSelectedValue } from '../../../hooks/filters/use-app-filter-selected-value.hook';
import { IMAGES_REPO } from '../../../static-resources';
import './app-force-selection.modal.scss';

interface AppForceSelectionModalProps {
  show: boolean;
  onClose?(show: boolean): void;
  title: string;
  enabled: boolean;
  filterSelection: FilterSelectedValue[];
  secondaryFilterIds?: string[];
  marketFilterId?: string;
}
export const AppForceSelectionModal = ({
  filterSelection,
  show,
  onClose,
  marketFilterId = FILTER_NAMES.MARKET,
}: AppForceSelectionModalProps) => {
  const [searchParams] = useSearchParams();
  const handleClose = () => onClose && onClose(false);
  const metaCategoryLabel = useAppSingleFilterSelectedLabel(
    FILTER_NAMES.METACATEGORY,
  );
  const { selectedValue: metaCategory } = useAppFilterSelectedValue(
    FILTER_NAMES.METACATEGORY,
  );
  const [noSel, setNoSel] = useState<boolean>(false);
  const { selectedValue: market } = useAppFilterSelectedValue(marketFilterId);
  const [noMetaCategory, setNoMetaCategory] = useState<boolean>(false);
  const [noMarket, setNoMarket] = useState<boolean>(false);

  useEffect(() => {
    const selVal = hasFilterSelectedValue(metaCategory);
    if (!selVal) {
      setNoSel(true);
    }
    setNoMetaCategory(selVal ? false : true);
  }, [metaCategory]);

  useEffect(() => {
    setNoMarket(!(market?.values && market.values.length > 0));
  }, [market]);

  const isDisabled = noMetaCategory || noMarket;

  const { handleResetFilter } = useFilterResetCallback();
  const [sel, setSel] = useState<string | boolean | undefined>();
  const [hasToReset, setHasToReset] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    // Captures Meta Category value to detect when it changes.
    const sel = hasFilterSelectedValue(metaCategory);
    if (sel) {
      setSel(`${sel.values.map((i) => i.value).join(',')}`);
    } else {
      setSel(false);
    }
  }, [metaCategory]);

  useEffect(() => {
    // Determines if next Meta Category change requires to reset the filters.
    // console.log(sel)
    if (sel) {
      // If the value is the initial load, we don't have to reset. But, subsequent change must reset.
      setHasToReset((v) => (v === undefined ? false : !v));
    }
    if (sel === false) {
      // This means Meta Category is empty, and next change must reset the filters.
      setHasToReset(false);
    }
  }, [sel]);

  useEffect(() => {
    if (hasToReset === true) {
      handleResetFilter(filterSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToReset, sel]);
  const navigate = useNavigate();
  const goBackHandler = useCallback(() => {
    if (!searchParams.get('ref')) {
      // It means that it cames from Okta redirect.
      navigate('/');
    } else {
      navigate(-1);
    }
  }, [navigate, searchParams]);
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        className="modalretailer"
        aria-labelledby="contained-modal-title-vcenter"
        centered={true}
      >
        <Modal.Body className="p-0">
          <div className="container-fluid p-0">
            <div className="row gy-2 align-items-center">
              <div className="col-lg-12">
                <div className="carticon bg-lightblue text-center">
                  <img src={IMAGES_REPO.ShoppingBag} alt="shoppingbag icon" />
                </div>
                <h3 className="familycareheading">
                  Please Select a {metaCategoryLabel} Retailer
                </h3>
                <p className="generaltext mb20">
                  Some retailers may be more strict on who’s allowed to see
                  them. If you think you should have access to something you
                  don’t, click&nbsp;
                  <NavLink
                    to="/help-center/request-access"
                    className="link-blue"
                  >
                    here
                  </NavLink>
                  &nbsp;to request access
                </p>

                {noSel && (
                  <div className="mb30 marketext">
                    <FilterPaneHeaderComponent
                      filterSelection={filterSelection}
                      variant="simple"
                      filterIds={[FILTER_NAMES.METACATEGORY]}
                      defaultLabel="Select"
                    />
                  </div>
                )}
                <div className="mb30 marketext">
                  <FilterPaneHeaderComponent
                    filterSelection={filterSelection}
                    variant="simple"
                    filterIds={[marketFilterId]}
                    defaultLabel="Select"
                  />
                </div>
                <div className="row">
                  <div className="col-lg-6">
                    <Button
                      onClick={goBackHandler}
                      className="goback-button d-flex justify-content-center align-items-center btn-default"
                    >
                      Go Back
                    </Button>
                  </div>
                  <div className="col-lg-6">
                    <ButtonPrimary
                      variant="primary"
                      className={`confirm-button d-flex justify-content-center align-items-center ${isDisabled ? 'disabled-look' : ''}`}
                      onClick={handleClose}
                    >
                      Confirm
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
